import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgIf } from '@angular/common';
import { AppConstants } from './app.constants';
import { ThemePermissionMatrixService } from 'src/_lib/service/theme-permission-matrix.service';
import { footerSetting, headerSetting, homeComponentSetting, homepageSettingsResponseInterface } from 'src/_lib/interfaces/homepagesettings.interface';
import { SeoService } from 'src/_lib/service/seo.service';
import { environment } from 'src/environments/environment.prod';
import { HeaderService } from 'src/_lib/service/header.service';
import { ScrollUpBtnComponent } from "./templates/scroll-up-btn/scroll-up-btn.component";
import { GtmService } from 'src/_lib/service/gtm.service';
import { Meta } from '@angular/platform-browser';
import { FooterComponent } from "./footer/footer.component";
import { HeaderComponent } from "./header/header.component";
import { BelakComingSoonComponent } from './templates/belak-coming-soon/belak-coming-soon.component';
import { BrowserService } from 'src/_lib/service/browser.service';
import { AppLoggerService } from 'src/_lib/service/app-logger.service';
import { TransferState, makeStateKey } from '@angular/core';

/** Key used to transfer homepage settings from SSR → client via TransferState. */
const HOMEPAGE_SETTINGS_KEY = makeStateKey<any>('homepageSettings');


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [NgIf, HeaderComponent, RouterOutlet, FooterComponent, ScrollUpBtnComponent,BelakComingSoonComponent]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  firstObj: number = this.appConstants.firstObj;
  title = environment.domainName;
  routerSub!: Subscription;
  seoSub!: Subscription;
  showFooterAfterDelay: boolean = false;
  homepageSettings: homepageSettingsResponseInterface<string> | null = null;
  headerSettings: headerSetting<string> | null = null;
  footerSettings: footerSetting<string> | null = null;
  routerOutletHomeSettings: homeComponentSetting<string> | null = null;
  cdnImagePath: string = environment.cdnImagePath;
  headerdata: any;
  isSidebarOpen = false;
  showComingSoon = false;
  // Start with 0 — real content fills the space, no artificial min-height causing layout shift
  routerOutletMinHeight = 0;

  @ViewChild('routerOutletRef') routerOutletRef!: ElementRef;

  constructor(
    private appConstants: AppConstants,
    private themePermissionMatrix: ThemePermissionMatrixService,
    private router: Router,
    private seoService: SeoService,
    private headerHttp: HeaderService,
    private gtmService: GtmService,
    private meta: Meta,
    private browser: BrowserService,
    private appLogger: AppLoggerService,
    private transferState: TransferState
  ) {
    // showComingSoon must be resolved in the constructor — both on SSR and browser —
    // so the initial render of app.component.html is identical on both sides.
    // Previously this was only set inside ngOnInit's browser block, causing a full
    // structural mismatch for the Belak domain (SSR rendered the full site,
    // client replaced it with the coming-soon component).
    const host = this.browser.doc?.location.hostname ?? '';
    const comingSoonDomains = ['www.belakindustries.com', 'belakindustries.com'];
    this.showComingSoon = comingSoonDomains.includes(host);

    if (this.browser.isBrowser) {
      // Priority 1: TransferState — homepage settings embedded in SSR HTML.
      const transferred = this.transferState.get(HOMEPAGE_SETTINGS_KEY, null);
      if (transferred) {
        this.applyHomepageSettings(transferred);
        this.transferState.remove(HOMEPAGE_SETTINGS_KEY);
      } else {
        // Priority 2: localStorage cache.
        this.preloadSettingsFromCache();
      }
    }
  }

  /**
   * FLASH FIX: Read homepage settings synchronously from localStorage in the
   * constructor so all header/footer/section settings are populated before the
   * first Angular render cycle. This prevents the null→data flash.
   * Used as a fallback when TransferState data is unavailable.
   */
  private preloadSettingsFromCache(): void {
    const stored = this.browser.localStorage?.getItem('homeSettingData');
    if (!stored) return;
    try {
      const response = JSON.parse(stored);
      if (response[this.firstObj]?.success === true) {
        this.applyHomepageSettings(response);
      }
    } catch {
      // Corrupt cache — will be refreshed by the API call in ngOnInit
    }
  }

  ngOnInit(): void {
  const isBrowser = this.browser.isBrowser;
  
  if (!isBrowser) {
    // SSR: load homepage settings only for the home route ("/"), then persist them
    // in TransferState so the client can read them synchronously on bootstrap
    // — this eliminates the flash even when localStorage is empty.
    // No other pages need homepage settings fetched or cached on the server.
    if (this.router.url === '/') {
      this.getHomePageComponentDisplay();
    }
    // Skip all browser-only setup (scroll, favicon, GTM, router events)
    return;
  }
  
  // --- Browser-only setup below ---

  // Initialize unified performance logging (respects APP_LOGS flag — zero cost if disabled)
  this.appLogger.initPerfMetrics();
  // Surface SSR pre-execution timing injected by server.ts into <head>
  this.appLogger.logSsrTimingFromMeta();
  
  // showComingSoon is now resolved in the constructor (both SSR + browser).
  if (this.showComingSoon) {
    return;
  }
  if (this.browser.history) {
    this.browser.history.scrollRestoration = 'manual';
  }
  
  const container = this.browser.doc?.documentElement;
  container?.removeAttribute('data-critters-container');
  
  // Load favicon in background (non-blocking)
  setTimeout(() => {
    this.loadFavicon();
  }, 1000);
  
  // Only fetch from API if cache did not already populate settings.
  // If cache was present, preloadSettingsFromCache() already set everything
  // in the constructor — no need to re-fetch on every load.
  if (this.router.url === '/' && !this.homepageSettings) {
    setTimeout(() => this.getHomePageComponentDisplay(), 0);
  }
  
  // Make SEO subscription passive (don't wait for it)
  this.seoSub = this.seoService.pageCode$
    .subscribe(code => this.seoService.loadSeo(code));
  
  // Make GTM loading non-blocking
  this.seoService.globalSeoSetting.subscribe(globalSeo => {
    if (globalSeo?.google_tag_manager_header && this.browser.isBrowser) {
      setTimeout(() => {
        this.gtmService.loadGtm(globalSeo.google_tag_manager_header);
      }, 1000);
    }
  });
  
  this.routerSub = this.router.events.subscribe(event => {
    if (event instanceof NavigationEnd) {      
      this.handleRouteChange();
      // HTML log: page render completed after navigation (guarded by APP_LOGS flag)
      this.appLogger.logHtmlRendered(this.router.url);
    }
  });
}

  private handleRouteChange(): void {
    this.setCanonicalURL();
    const rootRoute = this.router.routerState.root;
    const activeRoute = this.getDeepestRoute(rootRoute);
    const pageCode = activeRoute.snapshot.data?.['pageCode'];
    // this.seoService.resolvePageCode(pageCode);
    // if (!this.meta.getTag("name='robots'")) {
    //   this.meta.addTag({
    //     name: 'robots',
    //     content: 'index, follow'
    //   });
    // }
    this.browser.runInBrowser(() => {
  setTimeout(() => {
    this.browser.window?.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, 0);
});
  }

  private setRobotsTag(host: string): void {
    const content = (/-website\.nebhub\d*\.com$/i.test(host)) ? 'noindex, nofollow' : 'index, follow';
    const tag = this.meta.getTag("name='robots'");
    tag ? this.meta.updateTag({ name: 'robots', content }) : this.meta.addTag({ name: 'robots', content });
  }

  private getDeepestRoute(route: any): any {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  getHomePageComponentDisplay(): void {
    this.themePermissionMatrix.homepageSettings().subscribe({
      next: (response) => {
        if (response[this.firstObj]?.success === true) {
          this.applyHomepageSettings(response);
          // SSR: store in TransferState so the client reads it synchronously
          // on bootstrap before its first render cycle — preventing any flash.
          if (!this.browser.isBrowser) {
            this.transferState.set(HOMEPAGE_SETTINGS_KEY, response);
          }
        }
      },
      error: (error: Error) => {
        console.error('Unexpected error while fetching home page settings', error);
      }
    });
  }

  /**
   * Shared method used by both cache pre-load (constructor) and API response.
   * Parses the homepage-settings response and updates all bound properties.
   */
  private applyHomepageSettings(response: any): void {
    this.homepageSettings = response[this.firstObj]?.data || null;
    const headerType = this.homepageSettings?.body?.Headers?.selectedHeader || null;
    this.headerSettings =
      headerType === 'header1'
        ? this.homepageSettings?.body?.Headers?.header1 || null
        : headerType === 'header2'
          ? this.homepageSettings?.body?.Headers?.header2 || null
          : null;
    const footerType = this.homepageSettings?.body?.Footers?.selectedFooter || null;
    this.footerSettings =
      footerType === 'footer1'
        ? this.homepageSettings?.body?.Footers?.footer1 || null
        : footerType === 'footer2'
          ? this.homepageSettings?.body?.Footers?.footer2 || null
          : null;
    this.routerOutletHomeSettings = {
      Slider: this.homepageSettings?.body?.Slider || null,
      Carousel: this.homepageSettings?.body?.Carousel || null,
      featured_logo: this.homepageSettings?.body?.featured_logo || null,
      featured_category: this.homepageSettings?.body?.featured_category || null,
      featured_product: this.homepageSettings?.body?.featured_product || null,
      featured_gallery: this.homepageSettings?.body?.featured_gallery || null,
      blog_categories: this.homepageSettings?.body?.blog_categories || null,
      HomeArticles: this.homepageSettings?.body?.HomeArticles || null,
      project_vehicles: this.homepageSettings?.body?.project_vehicles || null,
      about_us: this.homepageSettings?.body?.about_us || null,
      reviews: this.homepageSettings?.body?.reviews || null,
      media_wrapper: this.homepageSettings?.body?.media_wrapper || null,
      HomePageSections: this.homepageSettings?.body?.HomePageSections || null,
    };
    this.themePermissionMatrix.routerOutletHomeSetting = this.routerOutletHomeSettings;
    this.themePermissionMatrix.routerOutletHomeSettingEmitter.next(null);
  }

  private setCanonicalURL(): void {
   if (!this.browser.doc) {
  return;
}
    const url = new URL(this.browser.doc.location.href);
    url.protocol = 'https:';
    url.search = '';
    url.hash = '';
    if (!url.pathname) {
      url.pathname = '/';
    }
    if (url.pathname !== '/' && url.pathname.endsWith('/')) {
      url.pathname = url.pathname.slice(0, -1);
    }
    const canonicalUrl = url.origin + url.pathname;
    let link = this.browser.doc.querySelector("link[rel='canonical']") as HTMLLinkElement;
    if (!link) {
      link = this.browser.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.browser.doc.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    // REMOVED: No longer needed since footer loads immediately
    // This prevents unnecessary scroll event handling
  }

  ngAfterViewInit(): void {
   if (
  this.browser.window &&
  'requestIdleCallback' in this.browser.window
) {
  (this.browser.window as any).requestIdleCallback(() => {
    this.browser.doc
      ?.getElementById('lcp-static-wrapper')
      ?.remove();
  });
}
  }

  loadFavicon(): void {
      if (!this.browser.isBrowser) {
    return;
  }
    this.headerHttp.headerList().subscribe(data => {
      this.headerdata = data[this.firstObj]?.data;
      const faviconPath = this.headerdata?.faviconIcon;
      if (faviconPath) {
        const faviconUrl = this.cdnImagePath + faviconPath;
        this.setFavicon(faviconUrl);
      } else {
        this.setFavicon('');
      }
    });
  }

  setFavicon(url: string): void {
    if (!this.browser.doc) {
  return;
}
    let link: HTMLLinkElement | null =
      this.browser.doc.querySelector("link[rel*='icon']");
    if (!link) {
      link = this.browser.doc.createElement('link');
      link.rel = 'icon';
      this.browser.doc.head.appendChild(link);
    }
    link.type = 'image/x-icon';
    link.href = url ? `${url}?v=${Date.now()}` : '';
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.seoSub?.unsubscribe();
  }
}